import React from "react";
import { Box, Container, Typography, Button, Stack } from "@mui/material";
import { Home, ArrowBack } from "@mui/icons-material";

interface ErrorPageLayoutProps {
  statusCode: number;
  title: string;
  message: string;
  children?: React.ReactNode;
  showHomeButton?: boolean;
  showBackButton?: boolean;
}

/**
 * Base layout for all error pages
 * Replaces Django error_base.html template
 */
export default function ErrorPageLayout({
  statusCode,
  title,
  message,
  children,
  showHomeButton = true,
  showBackButton = true,
}: ErrorPageLayoutProps): React.ReactElement {
  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box sx={{ textAlign: "center" }}>
        {/* Status Code */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "4rem", md: "6rem" },
            fontWeight: 800,
            color: "statusText.attention",
            mb: 2,
          }}
        >
          {statusCode}
        </Typography>

        {/* Title */}
        <Typography variant="h4" fontWeight={600} gutterBottom>
          {title}
        </Typography>

        {/* Message */}
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 600, mx: "auto" }}
        >
          {message}
        </Typography>

        {/* Custom Content */}
        {children && <Box sx={{ mb: 4 }}>{children}</Box>}

        {/* Action Buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          sx={{ mt: 4 }}
        >
          {showBackButton && (
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              size="large"
            >
              Go Back
            </Button>
          )}

          {showHomeButton && (
            <Button
              variant="contained"
              startIcon={<Home />}
              onClick={handleGoHome}
              size="large"
            >
              Go Home
            </Button>
          )}
        </Stack>

        {/* Additional Help Text */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          If you continue to experience issues, please contact support.
        </Typography>
      </Box>
    </Container>
  );
}
